import { Select as AntSelect } from 'antd'
import { SearchOutlined, PlusCircleFilled, CloseOutlined } from '@ant-design/icons'
import { Button, IconButton, Input, Modal } from '@/components/ui'
import type {
  SearchFilterModalProps,
  FilterCondition,
  FilterableField,
  FilterOperator,
} from './SearchFilterModal.types'

const DEFAULT_OPERATORS: { label: string; value: FilterOperator }[] = [
  { label: 'is', value: 'is' },
  { label: 'contains', value: 'contains' },
  { label: 'is not', value: 'not_equals' },
]

export function SearchFilterModal({
  open,
  onClose,
  title = 'Filter',
  description = 'Show items that match the following...',
  fields,
  filterConditions,
  onFilterConditionsChange,
  showNameSearch = true,
  nameSearchPlaceholder = 'Search name & keywords',
  nameSearchValue = '',
  onNameSearchChange,
  onApply,
  onReset,
  width = 700,
}: SearchFilterModalProps) {
  const addCondition = () => {
    onFilterConditionsChange([
      ...filterConditions,
      { field: '', operator: 'is', value: '' },
    ])
  }

  const removeCondition = (index: number) => {
    onFilterConditionsChange(filterConditions.filter((_, i) => i !== index))
  }

  const updateCondition = (index: number, key: keyof FilterCondition, value: string) => {
    const newConditions = [...filterConditions]
    newConditions[index] = { ...newConditions[index], [key]: value }

    // Clear value if field changes (to prevent stale values)
    if (key === 'field') {
      newConditions[index].value = ''
    }

    onFilterConditionsChange(newConditions)
  }

  const getSelectedField = (fieldIdentifier: string): FilterableField | undefined => {
    return fields.find(f => f.identifier === fieldIdentifier)
  }

  const renderValueInput = (condition: FilterCondition, index: number) => {
    const selectedField = getSelectedField(condition.field)

    if (selectedField?.type === 'select' || selectedField?.type === 'multiselect') {
      return (
        <AntSelect
          placeholder="Select value"
          className="flex-1"
          size="small"
          value={condition.value || undefined}
          onChange={(value) => updateCondition(index, 'value', value)}
        >
          {selectedField.options?.map((opt, optIdx) => (
            <AntSelect.Option key={optIdx} value={opt.value}>
              {opt.label}
            </AntSelect.Option>
          ))}
        </AntSelect>
      )
    }

    return (
      <Input
        placeholder="Enter value"
        className="flex-1"
        size="small"
        value={condition.value}
        onChange={(e) => updateCondition(index, 'value', e.target.value)}
      />
    )
  }

  return (
    <Modal
      title={title}
      open={open}
      onClose={onClose}
      width={width}
      footer={
        <div className="flex justify-end gap-2">
          <Button key="reset" variant="secondary" onClick={onReset}>
            Reset
          </Button>
          <Button key="apply" variant="primary" onClick={onApply}>
            Apply Filter
          </Button>
        </div>
      }
    >
      <div className="mb-4">
        <p className="text-sm text-gray-500 mb-4">{description}</p>

        {showNameSearch && onNameSearchChange && (
          <div className="mb-4">
            <label className="block text-sm mb-2">Name</label>
            <Input
              placeholder={nameSearchPlaceholder}
              value={nameSearchValue}
              onChange={(e) => onNameSearchChange(e.target.value)}
              suffix={<SearchOutlined className="text-gray-400" />}
            />
          </div>
        )}

        <div className="mb-4">
          <p className="text-sm mb-2">Filters</p>
          <div className="space-y-3">
            {filterConditions.map((condition, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="text-sm text-gray-500 w-16">where</span>
                <AntSelect
                  placeholder="Select field"
                  className="flex-1"
                  size="small"
                  value={condition.field || undefined}
                  onChange={(value) => updateCondition(index, 'field', value)}
                >
                  {fields.map(field => (
                    <AntSelect.Option key={field.id} value={field.identifier}>
                      {field.label}
                    </AntSelect.Option>
                  ))}
                </AntSelect>
                <AntSelect
                  placeholder="is"
                  className="w-32"
                  size="small"
                  value={condition.operator}
                  onChange={(value) => updateCondition(index, 'operator', value)}
                >
                  {DEFAULT_OPERATORS.map(op => (
                    <AntSelect.Option key={op.value} value={op.value}>
                      {op.label}
                    </AntSelect.Option>
                  ))}
                </AntSelect>
                {renderValueInput(condition, index)}
                <IconButton
                  icon={<CloseOutlined />}
                  size="small"
                  onClick={() => removeCondition(index)}
                  ariaLabel="Remove filter condition"
                  title="Remove"
                />
              </div>
            ))}
          </div>
          <Button
            variant="text"
            className="mt-3 px-0"
            onClick={addCondition}
            icon={<PlusCircleFilled />}
          >
            Add Condition
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export type {
  SearchFilterModalProps,
  FilterCondition,
  FilterableField,
  FilterOperator,
  FilterOption,
} from './SearchFilterModal.types'
